/* eslint-disable no-console */
import { generatedDataApi } from "apiClient/v2";
import { GenerateDataType } from "interfaces/models/generateData";
import pako from "pako";
import { fetchRequest } from "utils/fetch";

export interface ForgeDataImporterProps {
  // bimFileId?version=version
  urn: string;
  shouldCache?: boolean;
  level?: string;
}

abstract class ForgeDataImporter<T> {
  private urn: string;
  private responseFetchFile: Response | null = null;
  private shouldCache: boolean = false;
  private level?: string;

  abstract dataType: GenerateDataType;

  constructor(props: ForgeDataImporterProps) {
    this.urn = props.urn;
    this.shouldCache = props?.shouldCache ?? false;
    this.level = props?.level;
  }

  public async importData(): Promise<{
    data: T | undefined;
    error?: { isRegenerateData?: boolean };
  }> {
    try {
      const result = await generatedDataApi.getGeneratedDataByType({
        urn: this.urn,
        dataType: this.dataType,
        level: this.level,
        shouldCache: this.shouldCache,
      });
      const url = result?.data;
      if (!url) {
        return { data: undefined, error: { isRegenerateData: true } };
      }

      const response = await fetchRequest(url, {}, this.shouldCache);
      if (!response.ok) {
        return { data: undefined, error: { isRegenerateData: true } };
      }

      this.responseFetchFile = response;
      const data = await this.decompressData();

      return { data };
    } catch (error) {
      console.error(
        `Import failed (urn: ${this.urn}, dataType: ${this.dataType}): `,
        error
      );

      return { data: undefined };
    }
  }

  private async decompressData() {
    try {
      if (!this.responseFetchFile) {
        throw new Error(
          `Import failed (urn: ${this.urn}, dataType: ${this.dataType}): responseFetchFile not found.`
        );
      }

      const reader = this.responseFetchFile.body?.getReader();
      if (!reader) {
        throw new Error("ReadableStream error");
      }
      const inflator = new pako.Inflate();
      const decoder = new TextDecoder("utf-8");
      const result: any[] = [];
      let incompleteData = ""; // Buffer to hold incomplete chunks
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          inflator.push(new Uint8Array(0), true);
          break;
        }

        inflator.push(value, false);

        // Decompress value
        if (inflator.result) {
          if (typeof inflator.result === "string") {
            incompleteData += decoder.decode(value, { stream: true });
          } else {
            incompleteData += decoder.decode(inflator.result, { stream: true });
          }
        }

        // Split the buffer by newlines
        const chunks = incompleteData.split("\n");

        // Process all but the last chunk (it may be incomplete)
        for (let i = 0; i < chunks.length - 1; i++) {
          try {
            const jsonData = JSON.parse(chunks[i]);
            result.push(jsonData);
          } catch (err) {
            throw new Error(
              `Import failed (urn: ${this.urn}, dataType: ${this.dataType}): error parsing final JSON: `
            );
          }
        }

        incompleteData = chunks[chunks.length - 1];
      }

      // Handle any remaining buffered data after the loop
      if (incompleteData.trim() !== "") {
        try {
          const jsonData = JSON.parse(incompleteData);
          result.push(jsonData);
        } catch (error) {
          throw new Error(
            `Import failed (urn: ${this.urn}, dataType: ${this.dataType}): error parsing final JSON:`
          );
        }
      }

      const normalizedData = await this.normalizeData(result);

      return normalizedData;
    } catch (error) {
      console.error(
        `Import failed (urn: ${this.urn}, dataType: ${this.dataType}): `,
        error
      );

      return undefined;
    }
  }

  abstract normalizeData(_data: any[]): Promise<T>;
}

export default ForgeDataImporter;
